import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';

import LandingLayout from 'layouts/LandingLayout/LandingLayout';
import Header from 'Components/Header/Header';

import './docs.scss';

/**
 * The MCP docs table of contents, grouped the way the sidebar renders it.
 * Order here also drives the prev/next pagers, so a page's neighbours stay
 * correct when pages are added or moved.
 */
export const DOCS_NAV_GROUPS = [
  {
    label: 'Get started',
    items: [
      { to: '/docs/mcp/overview', label: 'Overview' },
      { to: '/docs/mcp/quickstart', label: 'Connect the connector' },
    ],
  },
  {
    label: 'Using it',
    items: [
      { to: '/docs/mcp/first-session', label: 'Your first session' },
      { to: '/docs/mcp/tools', label: 'Tool reference' },
      { to: '/docs/mcp/resources', label: 'Resources' },
    ],
  },
  {
    label: 'Clients',
    items: [{ to: '/docs/mcp/clients', label: 'Supported clients' }],
  },
  {
    label: 'Reference',
    items: [
      { to: '/docs/mcp/security', label: 'Authentication & security' },
      { to: '/docs/mcp/troubleshooting', label: 'Troubleshooting & FAQ' },
    ],
  },
];

/** Flat reading order, derived from the groups so the two cannot drift apart. */
export const DOCS_NAV = DOCS_NAV_GROUPS.flatMap((group) => group.items);

/**
 * Prev/next descriptors for a page, taken from the flat reading order.
 *
 * @param {string} path Route path of the current page.
 * @returns {{prev: object|undefined, next: object|undefined}}
 */
export const pagerFor = (path) => {
  const index = DOCS_NAV.findIndex((item) => item.to === path);
  if (index === -1) return { prev: undefined, next: undefined };
  return { prev: DOCS_NAV[index - 1], next: DOCS_NAV[index + 1] };
};

const COMPACT_QUERY = '(max-width: 991.98px)';

/** True when the viewport is narrow enough that the sidebar becomes a disclosure. */
const useIsCompact = () => {
  const [isCompact, setIsCompact] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(COMPACT_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia(COMPACT_QUERY);
    const onChange = (event) => setIsCompact(event.matches);
    setIsCompact(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    // Safari < 14
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  return isCompact;
};

/**
 * How far the reader has scrolled through the page, 0-1, for the progress rule
 * under the header. Cheap enough to run on scroll: one rAF-throttled read.
 */
const useReadingProgress = () => {
  const [progress, setProgress] = useState(0);
  const frame = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const read = () => {
      frame.current = 0;
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      setProgress(scrollable > 0 ? Math.min(1, window.scrollY / scrollable) : 0);
    };
    const onScroll = () => {
      if (frame.current) return;
      frame.current = window.requestAnimationFrame(read);
    };

    read();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (frame.current) window.cancelAnimationFrame(frame.current);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  return progress;
};

/** Shared clipboard write with a graceful fallback for older browsers. */
const writeToClipboard = async (text) => {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const area = document.createElement('textarea');
  area.value = text;
  area.setAttribute('readonly', '');
  area.style.position = 'fixed';
  area.style.opacity = '0';
  document.body.appendChild(area);
  area.select();
  document.execCommand('copy');
  document.body.removeChild(area);
};

/** Copy button that flips to a confirmation for two seconds. */
const CopyButton = ({ text, label, className }) => {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return undefined;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = useCallback(async () => {
    try {
      await writeToClipboard(text);
      setCopied(true);
    } catch (error) {
      setCopied(false);
    }
  }, [text]);

  return (
    <>
      <button
        type="button"
        className={className}
        onClick={handleCopy}
        data-copied={copied ? 'true' : undefined}
        aria-label={label ? `Copy ${label}` : 'Copy to clipboard'}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
      <span className="visually-hidden" aria-live="polite">
        {copied ? 'Copied to clipboard' : ''}
      </span>
    </>
  );
};

/**
 * Copy-to-clipboard code sample. Scrolls horizontally inside its own container,
 * so a long endpoint or command never widens the page.
 *
 * @param {object} props
 * @param {string} props.code      The literal text shown and copied.
 * @param {string} [props.label]   Small caption in the header bar (e.g. "Endpoint URL").
 * @param {string} [props.language] Value for the <code> element's data-language attribute.
 * @param {string} [props.className] Extra class names on the wrapper.
 */
export const CodeBlock = ({ code, label, language, className = '' }) => (
  <div className={`mcp-docs__codeblock ${className}`.trim()}>
    <div className="mcp-docs__codeblock-bar">
      <span className="mcp-docs__codeblock-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span className="mcp-docs__codeblock-label">
        {label || language || 'Code'}
      </span>
      <CopyButton
        text={code}
        label={label}
        className="mcp-docs__codeblock-copy"
      />
    </div>
    <pre className="mcp-docs__codeblock-pre" tabIndex={0}>
      <code data-language={language}>{code}</code>
    </pre>
  </div>
);

/**
 * A prompt to say to the agent, presented as a chat bubble rather than a code
 * sample &mdash; these are sentences a person types, not code they run.
 *
 * @param {object} props
 * @param {string} props.children The prompt text (also what gets copied).
 * @param {string} [props.note]   Optional caption under the bubble.
 */
export const PromptBlock = ({ children, note }) => (
  <div className="mcp-docs__prompt">
    <div className="mcp-docs__prompt-bubble">
      <span className="mcp-docs__prompt-tag">Ask your agent</span>
      <p className="mcp-docs__prompt-text">{children}</p>
      <CopyButton
        text={typeof children === 'string' ? children : ''}
        label="prompt"
        className="mcp-docs__prompt-copy"
      />
    </div>
    {note && <p className="mcp-docs__prompt-note">{note}</p>}
  </div>
);

/**
 * Aside for something the reader needs to notice.
 *
 * @param {object} props
 * @param {'note'|'tip'|'warn'|'success'} [props.tone] Visual treatment.
 * @param {string} [props.title] Bold first line.
 */
export const Callout = ({ tone = 'note', title, children }) => (
  <aside className={`mcp-docs__callout mcp-docs__callout--${tone}`}>
    {title && <p className="mcp-docs__callout-title">{title}</p>}
    {children}
  </aside>
);

/** Bottom-of-page previous / next links. */
const Pager = ({ prev, next }) => {
  if (!prev && !next) return null;
  return (
    <nav className="mcp-docs__pager" aria-label="Documentation page navigation">
      {prev ? (
        <Link className="mcp-docs__pager-link mcp-docs__pager-link--prev" to={prev.to}>
          <span className="mcp-docs__pager-dir">Previous</span>
          <span className="mcp-docs__pager-title">{prev.label}</span>
        </Link>
      ) : (
        <span className="mcp-docs__pager-spacer" aria-hidden="true" />
      )}
      {next && (
        <Link className="mcp-docs__pager-link mcp-docs__pager-link--next" to={next.to}>
          <span className="mcp-docs__pager-dir">Next</span>
          <span className="mcp-docs__pager-title">{next.label}</span>
        </Link>
      )}
    </nav>
  );
};

/**
 * Shell for every page under /docs/mcp/*.
 *
 * @param {object} props
 * @param {string} [props.eyebrow] Small label above the title.
 * @param {string} [props.title]   Page <h1>. Omit to render your own heading.
 * @param {React.ReactNode} [props.lede] Standfirst paragraph under the title.
 * @param {Array<{label: string, value: string}>} [props.stats] Facts beside the title.
 * @param {Array<{href: string, label: string}>} [props.toc] "On this page" chips.
 * @param {object} [props.prev] Pager target: { to, label }.
 * @param {object} [props.next] Pager target: { to, label }.
 */
const DocsLayout = ({
  children,
  eyebrow,
  title,
  lede,
  stats,
  toc,
  prev,
  next,
}) => {
  const { pathname } = useLocation();
  const isCompact = useIsCompact();
  const [navOpen, setNavOpen] = useState(false);
  const progress = useReadingProgress();

  // Collapse the mobile disclosure after navigating.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  const sidebarShown = !isCompact || navOpen;

  return (
    <LandingLayout>
      <Header />
      <div className="mcp-docs">
        <div
          className="mcp-docs__progress"
          aria-hidden="true"
          style={{ transform: `scaleX(${progress})` }}
        />
        <Container>
          <button
            type="button"
            className="mcp-docs__sidebar-toggle"
            aria-expanded={navOpen}
            aria-controls="mcp-docs-nav"
            onClick={() => setNavOpen((open) => !open)}
          >
            <span>Documentation menu</span>
            <span className="mcp-docs__sidebar-toggle-icon" aria-hidden="true">
              {navOpen ? '−' : '+'}
            </span>
          </button>

          <div className="mcp-docs__shell">
            <nav
              id="mcp-docs-nav"
              className="mcp-docs__sidebar"
              aria-label="MCP documentation"
              hidden={!sidebarShown}
            >
              <div className="mcp-docs__sidebar-inner">
                <p className="mcp-docs__sidebar-heading">MCP connector</p>
                {DOCS_NAV_GROUPS.map((group) => (
                  <div className="mcp-docs__nav-group" key={group.label}>
                    <p className="mcp-docs__nav-group-label">{group.label}</p>
                    <ul className="mcp-docs__nav-list">
                      {group.items.map((item) => {
                        const isActive =
                          pathname === item.to || pathname === `${item.to}/`;
                        return (
                          <li key={item.to}>
                            <Link
                              to={item.to}
                              className={`mcp-docs__nav-link${
                                isActive ? ' mcp-docs__nav-link--active' : ''
                              }`}
                              aria-current={isActive ? 'page' : undefined}
                            >
                              {item.label}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </nav>

            <main className="mcp-docs__content" id="mcp-docs-content">
              {(eyebrow || title || lede) && (
                <header className="mcp-docs__hero">
                  {eyebrow && <p className="mcp-docs__eyebrow">{eyebrow}</p>}
                  {title && <h1>{title}</h1>}
                  {lede && <p className="mcp-docs__lede">{lede}</p>}
                  {stats && stats.length > 0 && (
                    <ul className="mcp-docs__stats">
                      {stats.map((stat) => (
                        <li key={stat.label}>
                          <strong>{stat.value}</strong>
                          <span>{stat.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </header>
              )}

              {toc && toc.length > 0 && (
                <nav className="mcp-docs__toc" aria-label="On this page">
                  <span className="mcp-docs__toc-label">On this page</span>
                  <ul>
                    {toc.map((item) => (
                      <li key={item.href}>
                        <a href={item.href}>{item.label}</a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              {children}
              <Pager prev={prev} next={next} />
            </main>
          </div>
        </Container>
      </div>
    </LandingLayout>
  );
};

export default DocsLayout;
